<template>
	<ClientTeleport>
		<Transition name="fade">
			<Modal
				v-if="isOpen"
				:is-open="isOpen"
				hide-container
				align-top
				@update:is-open="onModalOpenChange"
			>
				<div class="relative">
					<div
						v-if="$slots.navigation"
						class="absolute top-40 md:top-56 left-0 lg:-left-6 right-0 lg:-right-6 z-20 flex justify-between pointer-events-none"
					>
						<slot name="navigation" />
					</div>
					<div class="py-16 px-4 md:px-8 grid grid-cols-1 lg:grid-cols-[5.4fr_6.6fr] gap-4 sm:gap-8 lg:gap-10">
						<LeafletProductModalSkeleton v-if="catalogPending" />
						<template v-else-if="catalogProduct?.vanr && productInfo">
							<div class="relative flex items-start justify-center min-w-0">
								<ProductRibbons
									:product="catalogProduct"
									class="absolute left-0 top-0 z-10 overflow-hidden"
									is-product-detail
								/>
								<ProductImage
									:product="catalogProduct"
									variant="detail"
									class="w-full rem:max-h-[200px] sm:rem:max-h-[450px] object-contain max-lg:max-w-[21.4rem]"
									loading="lazy"
								/>
								<ProductBadges
									v-if="badges"
									class="absolute inset-0"
									:product="catalogProduct"
									:badges="badges"
									:age-limitation="catalogProduct?.ageLimitation ?? undefined"
								/>
								<ProductOutOfStockBar :product="catalogProduct" />
							</div>
							<div>
								<div class="flex flex-col gap-2 lg:gap-4 items-start max-md:justify-between auto-cols-mins mb-6 lg:mb-10 min-w-0">
									<h2 class="typo-h1 text-heading-primary">
										{{ catalogProduct?.name ?? '' }}
									</h2>
									<ProductAmountInfo
										:product="catalogProduct"
										class="md:col-span-2 hidden md:flex"
									/>
									<InformationAboutHypermarket
										text="Informace pro"
										class="max-md:hidden col-span-2"
										:default-hm="DEFAULT_HYPERMARKET"
										hide-change-button
									/>
									<div
										v-if="priceValidityText"
										class="typo-body-m bg-functional-error-primary text-body-inverse rounded-lg p-2 font-bold"
									>
										{{ priceValidityText }}
									</div>
									<div class="w-full flex gap-4 justify-between items-start">
										<LeafletPricing
											:product-info="productInfo"
											large
										/>
										<ProductAvailabilityCard
											:product="catalogProduct"
											class="max-w-[21.4rem] max-lg:w-full"
											hide-button
										/>
									</div>
									<ProductButtons
										v-model:is-shopping-list-open="isShoppingListOpen"
										:product="shoppingListProduct"
										:disabled="catalogPending"
										product-detail
										button-style
										method="DL_modal"
									/>
									<LazyProductDetailMoreVariants
										v-if="moreVariants?.length"
										:variants="moreVariants"
										:current-variant="catalogProduct"
										:route-generator="productVariantRouteGenerator"
										class="w-full"
									/>
								</div>
								<ProductLoyaltyBanners :product="catalogProduct" />
								<ProductDetailInfo
									:product="catalogProduct"
									:hide-price-validity="hidePriceValidity"
								/>
							</div>
						</template>
						<template v-else-if="productInfo">
							<div class="flex items-center justify-center min-w-0 min-h-96">
								<img
									:src="globusPlaceholder"
									class="w-full rem:max-h-[150px] object-contain"
									:alt="productInfo.Name || ''"
									loading="lazy"
								/>
							</div>
							<div>
								<div class="flex flex-col gap-2 lg:gap-4 items-start max-md:justify-between auto-cols-mins mb-6 lg:mb-10 min-w-0">
									<h2 class="typo-h1 text-heading-primary">
										{{ productInfo.Name }}
									</h2>
									<div class="flex gap-1 md:gap-4 max-md:flex-col items-end md:items-center flex-wrap">
										<span v-if="productInfo.quantityUnit && productInfo.quantity" class="text-xs md:text-xl text-body-tertiary md:font-semibold md:font-secondary max-md:bg-light-300 max-md:p-1 max-md:rounded-lg">
											{{ productInfo.quantity }} {{ productInfo.quantityUnit }}
										</span>
										<span v-if="productInfo.unitPrice" class="text-body-quaternary text-xs md:text-sm flex md:gap-2 flex-col md:flex-row text-end">
											{{ productInfo.unitPrice }}
										</span>
									</div>
									<InformationAboutHypermarket
										text="Informace pro"
										class="max-md:hidden col-span-2"
										:default-hm="DEFAULT_HYPERMARKET"
										hide-change-button
									/>
									<div v-if="priceValidityText" class="typo-body-m bg-functional-error-primary text-body-inverse rounded-lg p-2 font-bold">
										{{ priceValidityText }}
									</div>
									<LeafletPricing
										:product-info="productInfo"
										large
									/>
									<ProductButtons
										v-model:is-shopping-list-open="isShoppingListOpen"
										:product="shoppingListProduct"
										:disabled="catalogPending"
										product-detail
										button-style
										method="DL_modal"
									/>
								</div>
								<ProductLoyaltyBanners />
								<template v-if="productInfo.shortDescription">
									<h2 class="typo-h2 text-left text-heading-primary mb-2">
										Popis produktu
									</h2>
									<p class="mb-4 text-body-secondary typo-body-s" v-html="productInfo.shortDescription" />
								</template>
							</div>
						</template>
					</div>
				</div>
			</Modal>
		</Transition>
	</ClientTeleport>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import type {RouteLocationRaw} from 'vue-router';
	import type {ProductInfo} from '@globus/image-overlay';
	import {
		ThisAndThatService,
		type Gsoa_ProductCatalog_ProductDetail as ProductDetail,
	} from '~/openapi';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import {virtualCategories, useActionOffer} from '~/stores/actionOffer';
	import {DEFAULT_HYPERMARKET} from '#shared/constants';
	import {createHouseProductSlug} from '~/utils/createHouseProductSlug';
	import {getProductCategorySlug} from '~/utils/products';
	import {Badge} from '@/types/product';
	import globusPlaceholder from '@/assets/icons/custom/globus.svg?url';

	type Props = {
		productInfo: ProductInfo | null;
		priceValidityText?: string | null;
		hidePriceValidity?: boolean;
	};
	const props = defineProps<Props>();

	const isOpen = defineModel<boolean>('isOpen', {default: false});
	const isShoppingListOpen = ref(false);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const houseNumber = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);

	const customerProfileStore = useCustomerProfileStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);

	const actionOfferStore = useActionOffer();
	const {lastVisitedCategory, categories} = storeToRefs(actionOfferStore);
	const branchSpecificPath = useBranchSpecificPath();

	const {
		catalogProduct,
		pending: catalogPending,
		shoppingListProduct,
	} = useLeafletCatalogProduct(() => props.productInfo, {enabled: isOpen});

	const {data: moreVariants} = useLazyAsyncData(
		`leaflet-more-variants-${catalogProduct.value?.vanr}-${houseNumber.value}`,
		async () => {
			const vanr = catalogProduct.value?.vanr;
			if (!isOpen.value || !houseNumber.value || !vanr || !userIsLogged.value) {
				return null;
			}

			const response = await ThisAndThatService.thisAndThatGetProductsOnSaleCatalog({
				vanr,
				marketId: Number(houseNumber.value),
				strategy: '',
			});

			return response.products ?? null;
		},
		{
			watch: [isOpen, houseNumber, () => catalogProduct.value?.vanr],
			server: false,
			default: () => null,
		},
	);

	const variantCategorySlug = computed(() => {
		const category = categories.value?.find(
			(cat) => cat.categoryId === lastVisitedCategory.value,
		);

		return getProductCategorySlug(category ?? virtualCategories.myGlobusPrices);
	});

	const badges = computed(() =>
		catalogProduct.value?.badges
			? {
				...catalogProduct.value?.badges,
				...(moreVariants.value?.length ? createProductBadges(Badge.MORE_VARIANTS) : undefined),
			}
			: null,
	);

	function productVariantRouteGenerator(variant: ProductDetail): RouteLocationRaw {
		return branchSpecificPath({
			name: 'hypermarket-akcni-nabidka-category-p-product',
			params: {
				category: variantCategorySlug.value,
				product: createHouseProductSlug(variant),
			},
		}).value;
	}

	function onModalOpenChange(open: boolean) {
		isOpen.value = open;
	}
</script>
